import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide3TechSupport.copy'

const ease = [0.16, 1, 0.3, 1] as const

const STEP_DURATION = 1800
const STEP_DONE = 4
type Step = 0 | 1 | 2 | 3 | 4
type Section = 1 | 2 | 3

function spotlight(section: Section, step: Step) {
  if (step === 0 || step === STEP_DONE) return 1
  return step === section ? 1 : 0.25
}

function highlightBg(section: Section, step: Step) {
  return step === section
    ? 'rgba(254, 240, 138, 0.7)'
    : 'rgba(254, 240, 138, 0)'
}

export default function Slide3TechSupport({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['fakeNumber'],
  })
  const [step, setStep] = useState<Step>(0)

  const SECTIONS = [
    { key: 'trust', label: c.trustLabel, items: [c.trustItem1, c.trustItem2], num: 1 as Section },
    { key: 'emotion', label: c.emotionLabel, items: [c.pressureFear, c.pressureUrgency], num: 2 as Section },
    { key: 'action', label: c.actionLabel, items: [c.actionItem], num: 3 as Section },
  ]

  const handleReveal = () => {
    if (step !== 0) return
    setStep(1)
    onComplete()
  }

  useEffect(() => {
    if (step === 0 || step >= STEP_DONE) return
    const t = setTimeout(() => setStep((s) => (s + 1) as Step), STEP_DURATION)
    return () => clearTimeout(t)
  }, [step])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 max-w-4xl mx-auto">
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8 w-full text-start"
      >
        {c.title}
      </motion.h2>

      {/* Horizontal layout: message left, pattern right */}
      <div className="w-full grid grid-cols-1 sm:grid-cols-[4fr_auto_3fr] gap-6 sm:gap-8 items-center">
        {/* Browser popup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4, ease }}
        >
          <div className="rounded-lg border border-slate-300 bg-white shadow-xl overflow-hidden text-start">
            <div className="flex items-center gap-2 bg-slate-100 border-b border-slate-200 px-3 py-2">
              <div className="flex gap-1.5">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </div>
            </div>
            <div className="bg-gradient-to-b from-red-50 to-white px-5 py-5">
              <motion.div
                initial={false}
                animate={{ opacity: spotlight(1, step), backgroundColor: highlightBg(1, step) }}
                transition={{ duration: 0.6, ease }}
                className="flex items-center gap-3 mb-3 rounded-md"
              >
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <p className="text-base font-bold text-red-700 uppercase tracking-wide">
                  {c.warningHeader}
                </p>
              </motion.div>
              <motion.p
                initial={false}
                animate={{ opacity: spotlight(2, step), backgroundColor: highlightBg(2, step) }}
                transition={{ duration: 0.6, ease }}
                className="text-base text-slate-800 leading-relaxed rounded"
              >
                <span className="font-bold">{c.warningBodyBold}</span>
                {c.warningBody}
              </motion.p>
              <p className="mt-3 text-base text-slate-800 leading-relaxed">
                <motion.span
                  initial={false}
                  animate={{
                    opacity: step === 0 || step === STEP_DONE ? 1 : step === 2 || step === 3 ? 1 : 0.25,
                    backgroundColor: step === 2 || step === 3 ? 'rgba(254, 240, 138, 0.7)' : 'rgba(254, 240, 138, 0)',
                  }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  {c.callPrefix}
                </motion.span>
                <motion.span
                  initial={false}
                  animate={{ opacity: spotlight(1, step), backgroundColor: highlightBg(1, step) }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  {c.brandName}
                </motion.span>
                <motion.span
                  initial={false}
                  animate={{ opacity: spotlight(3, step), backgroundColor: highlightBg(3, step) }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  {c.callMid}
                </motion.span>
                <motion.span
                  initial={false}
                  animate={{
                    opacity: step === 0 || step === STEP_DONE ? 1 : step === 2 || step === 3 ? 1 : 0.25,
                    backgroundColor: step === 2 || step === 3 ? 'rgba(254, 240, 138, 0.7)' : 'rgba(254, 240, 138, 0)',
                  }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  {c.callImmediately}
                </motion.span>
                <motion.span
                  initial={false}
                  animate={{ opacity: spotlight(3, step), backgroundColor: highlightBg(3, step) }}
                  transition={{ duration: 0.6, ease }}
                  className="rounded [box-decoration-break:clone]"
                >
                  :{' '}
                  <span dir="ltr" className="font-bold text-blue-700">{c.fakeNumber}</span>
                </motion.span>
              </p>
              <motion.p
                initial={false}
                animate={{ opacity: spotlight(2, step), backgroundColor: highlightBg(2, step) }}
                transition={{ duration: 0.6, ease }}
                className="mt-3 text-xs font-bold text-red-600 uppercase rounded inline-block"
              >
                {c.doNotClose}
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div
          animate={{ opacity: step >= 1 ? 1 : 0 }}
          transition={{ duration: 0.4, delay: step >= 1 ? 0.15 : 0 }}
          className="hidden sm:flex items-center justify-center"
        >
          <svg width="32" height="24" viewBox="0 0 32 24" fill="none" className="text-slate-300 dir-flip">
            <path d="M0 12h24m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>

        {/* Right side: button or pattern breakdown */}
        <div className="flex items-center">
          {step === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7, ease }}
              className="w-full flex justify-center sm:justify-start"
            >
              <button
                onClick={handleReveal}
                className="rounded-full border border-slate-200 px-8 py-3.5 text-lg font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer"
              >
                {c.revealButton}
              </button>
            </motion.div>
          ) : (
            <div className="text-start space-y-7 w-full">
              {SECTIONS.map((s) => {
                const isVisible = step >= s.num
                const isActive = step === s.num
                const targetOpacity = step === STEP_DONE ? 1 : isActive ? 1 : isVisible ? 0.5 : 0
                return (
                  <motion.div
                    key={s.key}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: targetOpacity, x: isVisible ? 0 : 12 }}
                    transition={{ duration: 0.6, ease }}
                  >
                    <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                      {s.num}. {s.label}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {s.items.map((item, i) => (
                        <li key={i} className="text-base sm:text-lg text-slate-500 leading-snug">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
